/**
 * history_page_v3.js — DAS-104 Phase 02: History 시안 V2 매핑 (시안 HistoryDesktopV2 + HistoryMobileV2)
 *
 * 원본: /tmp/design-pkg/mydashboard/project/pages/history.jsx
 * 매핑: HistoryDesktopV2 + HistoryMobileV2 → 이 파일 (desktop + mobile 반응형)
 *
 * UI 구성 (시안 HistoryDesktopV2 기준):
 *   1. 4-KPI 요약 카드 행 (repeat(4, 1fr) gap=14)
 *   2. 잔고 추이 (1fr) + 월별 손익 사이드 (340px) 2-col 그리드
 *   3. 활동 타임라인 — 전략별 탭 + HistoryChart + HistoryTable
 *   4. (desktop) 별도 월별 잔고 요약 테이블 카드
 *
 * UI 구성 (시안 HistoryMobileV2 기준):
 *   1. 이번 달 손익 히어로 카드 (gradient + radial 글로우)
 *   2. 미니 KPI 2-col (총 잔고 / 누적 기간)
 *   3. 잔고 추이 카드
 *   4. 월별 요약 (미니바 헤더 우측 + 목록)
 *   5. 활동 카드
 *   6. 월별 잔고 요약 테이블 카드
 *
 * 시안 ↔ 운영 매핑:
 *   - 시안 PnlChart           → TotalBalanceChart (운영 컴포넌트)
 *   - 시안 MiniBars           → primitives.MiniBars (data: [{ v: m.pct }])
 *   - 시안 KpiValue           → 인라인 스타일 (primitives.KpiValue 직접 사용 가능하나 size=22 고정으로 인라인)
 *   - 시안 4-KPI 카드         → 운영 데이터 (총잔고/전략수/이번달수익률/누적기간)로 매핑
 *   - 시안 활동 타임라인       → HistoryChart + HistoryTable (전략 탭)
 *   - 시안 TimelineRow detail → TODO(DAS-104): BE 미제공 (진입가/체결가/슬리피지)
 *
 * BE gap:
 *   - G4 (월별 집계): FE buildMonthlyBars()로 클라이언트 집계
 *   - H3-B (detail strip): BE 미제공 → 전략 탭으로 대체
 *   - 시안 V2 "더 보기 · 241건" 버튼: TODO(DAS-104) — 페이지네이션 미구현
 *
 * DAS-84 대비 변경:
 *   - MiniBars: 직접 SVG 구현(useThemeColors 훅) → primitives.MiniBars 교체
 *   - data 형태: { pct } → { v: m.pct } (primitives API 맞춤)
 *   - 시안 "더 보기" Btn 추가 (TODO placeholder)
 *
 * silver 토큰 전용. JetBrains Mono 유지. 하드코딩 색상(#hex) 금지.
 */

import React from 'react';
import dayjs from 'dayjs';
import NavBar from '../common/nav_bar';
import MaintenanceBanner from '../common/maintenance_banner';
import ScrollToTopButton from '../common/scroll_to_top_button';
import FilterPill from '../common/filter_pill';
import TotalBalanceChart from '../history_page/total_balance_chart';
import HistoryTable from '../history_page/history_table';
import MonthlySummaryTable from '../history_page/monthly_summary_table';
import HistoryChart from '../history_page/history_chart';
import { fetchBalanceHistory, fetchMonthlySummary } from '../../services/balanceApi';
import { getStoredUsername, requireLoginOrThrow } from '../../utils/auth';
import { getNavCollapsed, setNavCollapsed } from '../../utils/navState';
import { isMobileViewport } from '../../utils/responsive';
import { V2Icon, V2Badge, KpiLabel, MiniBars, Btn } from '../common/claude_primitives';
import { showToast } from '../../utils/show_toast';

/* ── 숫자 포맷 헬퍼 ──────────────────────────────────────── */
const krw0 = (n) => {
    const num = Number(n) || 0;
    return '₩' + Math.round(num).toLocaleString();
};

const pctStr = (n) => {
    const num = Number(n) || 0;
    return (num >= 0 ? '+' : '') + num.toFixed(2) + '%';
};

/* ── NOTE(DAS-65-G4): FE 집계 — monthly bars ─────────────── */
const buildMonthlyBars = (balanceHistory) => {
    const monthMap = new Map();
    (balanceHistory || []).forEach((strategyArr) => {
        (strategyArr || []).forEach((item) => {
            if (!item?.created) return;
            const monthKey = dayjs(item.created).format('YYYY-MM');
            const totalBal = Number(item.total_balance || 0);
            if (!monthMap.has(monthKey)) {
                monthMap.set(monthKey, { first: totalBal, last: totalBal });
            } else {
                const cur = monthMap.get(monthKey);
                monthMap.set(monthKey, { first: cur.first, last: totalBal });
            }
        });
    });
    const months = Array.from(monthMap.keys()).sort();
    return months.map((month, i) => {
        const { first, last } = monthMap.get(month);
        const prevMonthKey = months[i - 1];
        const startBalance = prevMonthKey ? monthMap.get(prevMonthKey).last : first;
        const pnl = last - startBalance;
        const pct = startBalance !== 0 ? (pnl / startBalance) * 100 : 0;
        return { month, start: startBalance, end: last, pnl, pct, pos: pct >= 0 };
    }).reverse(); /* 최신 월부터 */
};

/* ── KPI 카드 (시안 V2 .c { padding: 18 } + KpiValue size: 22) ── */
function KpiCard({ label, value, sub, tone, delta }) {
    return (
        <div
            className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
            style={{ padding: 18, display: 'flex', flexDirection: 'column' }}
        >
            <KpiLabel>{label}</KpiLabel>
            <div style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 22, fontWeight: 600,
                letterSpacing: '-0.015em',
                color: tone ? `var(--color-${tone})` : 'var(--color-content)',
                marginTop: 2, lineHeight: 1.05,
            }}>
                {value}
            </div>
            {delta != null && (
                <div style={{ marginTop: 6 }}>
                    <span style={{
                        color: delta >= 0 ? 'var(--color-gain)' : 'var(--color-loss)',
                        fontSize: 12, fontWeight: 600,
                        fontFamily: 'JetBrains Mono, monospace',
                    }}>
                        {delta >= 0 ? '+' : ''}{Number(delta).toFixed(2)}%
                    </span>
                </div>
            )}
            {sub && (
                <div style={{
                    marginTop: 6, fontSize: 10,
                    color: 'var(--color-content-muted)',
                    fontFamily: 'JetBrains Mono, monospace',
                }}>
                    {sub}
                </div>
            )}
        </div>
    );
}

/* ── 카드 헤더 (시안 .c-head — align-items: baseline) ───── */
function CardHead({ title, badge, sub, right }) {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 12,
            gap: 10,
            flexWrap: 'wrap',
        }}>
            <div style={{ minWidth: 0 }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: 'var(--color-content)',
                }}>
                    <span>{title}</span>
                    {badge}
                </div>
                {sub && (
                    <div style={{
                        marginTop: 4,
                        fontSize: 12,
                        color: 'var(--color-content-secondary)',
                    }}>
                        {sub}
                    </div>
                )}
            </div>
            {right && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {right}
                </div>
            )}
        </div>
    );
}

/* ── 월별 손익 행 (시안 V2 사이드 카드 행 — 3-col) ──────── */
function MonthlyRow({ m }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 0',
        }}>
            <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 12,
                color: 'var(--color-content-secondary)',
            }}>
                {m.month}
            </span>
            <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 12, fontWeight: 600,
                color: m.pos ? 'var(--color-gain)' : 'var(--color-loss)',
            }}>
                {m.pos ? '+' : ''}{krw0(m.pnl)}
            </span>
            <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 12, fontWeight: 600,
                color: m.pos ? 'var(--color-gain)' : 'var(--color-loss)',
                minWidth: 56, textAlign: 'right',
            }}>
                {pctStr(m.pct)}
            </span>
        </div>
    );
}

/* ── 새로고침 버튼 ─────────────────────────────────────────── */
function RefreshBtn({ loading, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={loading}
            aria-label="데이터 새로고침"
            className="flex items-center gap-1 rounded-lg border border-surface-border px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-hover disabled:opacity-50 transition-colors"
        >
            {loading ? (
                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
            ) : (
                <V2Icon name="refresh" size={12} />
            )}
            <span className="hidden sm:inline">{loading ? '갱신 중...' : '새로고침'}</span>
        </button>
    );
}

/* ── 스피너 ──────────────────────────────────────────────── */
function Spinner() {
    return (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
            <svg
                className="inline h-5 w-5 animate-spin text-content-muted"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
            >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
        </div>
    );
}

/* ── 데스크톱 V3 (시안 HistoryDesktopV2 매핑) ───────────── */
function HistoryDesktopV2Refreshed({ state, handlers }) {
    const {
        loading, lastUpdated, balanceHistory, monthlySummary,
        historyMode, selectedMonth, availableMonths, activeTabKey,
        chartTimeRange,
    } = state;
    const {
        setHistoryMode, handleMonthChange, handleMonthFetch,
        handleRefresh, setActiveTabKey, setChartTimeRange,
    } = handlers;

    /* NOTE(DAS-65-G4): FE 집계 */
    const monthlyBars = buildMonthlyBars(balanceHistory);
    const currentMonth = monthlyBars[0] || null;

    /* KPI 집계 */
    const latestTotals = (balanceHistory || []).map((arr) => arr?.[0]);
    const totalBalance = latestTotals.reduce((a, s) => a + Number(s?.total_balance || 0), 0);

    /* 탭 목록 */
    const tabs = (balanceHistory || [])
        .slice()
        .sort((a, b) => (b[0]?.total_balance || 0) - (a[0]?.total_balance || 0))
        .map((hist, i) => {
            const strategyName = hist[0]?.strategy_name || 'default';
            const fiatEx = hist[0]?.fiat_exchange;
            const riskyEx = hist[0]?.risky_exchange;
            const pairLabel = fiatEx && riskyEx
                ? `${fiatEx}-${riskyEx}`
                : fiatEx ? `${fiatEx} (현물)` : riskyEx ? `${riskyEx} (선물)` : 'unknown';
            return { key: String(i), label: `${strategyName} (${pairLabel})`, hist };
        });

    const resolvedActiveKey = tabs.find((t) => t.key === activeTabKey)?.key || tabs[0]?.key;
    const activeTab = tabs.find((t) => t.key === resolvedActiveKey);

    const formatLastUpdated = (date) => {
        if (!date) return '';
        const h = String(date.getHours()).padStart(2, '0');
        const m = String(date.getMinutes()).padStart(2, '0');
        const s = String(date.getSeconds()).padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    /* 잔고 추이 카드 Badge — 시안 V2 c-head 인라인 */
    const balanceBadge = currentMonth
        ? <V2Badge tone={currentMonth.pos ? 'gain' : 'loss'}>{pctStr(currentMonth.pct)}</V2Badge>
        : null;

    /* primitives.MiniBars 용 data 변환 — { v: pct } */
    const miniBarsData = [...monthlyBars].reverse().map((m) => ({ v: m.pct }));

    return (
        /* 시안 .app-body gap=24 (V2) */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* 페이지 헤더 — 시안 V2 TopBar (title + subtitle) */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <div style={{
                        fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
                        color: 'var(--color-content-muted)', fontFamily: 'JetBrains Mono, monospace',
                        marginBottom: 4,
                    }}>
                        홈 · 거래 내역
                    </div>
                    <h1 style={{
                        fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em',
                        color: 'var(--color-content)', margin: 0,
                    }}>
                        History
                    </h1>
                    <div style={{
                        fontSize: 12,
                        color: 'var(--color-content-secondary)',
                        marginTop: 4,
                    }}>
                        거래 · 잔고 변동 · 월간 집계를 타임라인 기준으로
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {lastUpdated && (
                        <span style={{
                            fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                            color: 'var(--color-content-muted)',
                        }}>
                            {formatLastUpdated(lastUpdated)} 갱신
                        </span>
                    )}
                    <RefreshBtn loading={loading} onClick={handleRefresh} />
                </div>
            </div>

            {/* 조회 필터 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                <span style={{
                    fontSize: 11, color: 'var(--color-content-muted)',
                    fontFamily: 'JetBrains Mono, monospace',
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>
                    조회
                </span>
                <FilterPill active={historyMode === '30d'} onClick={() => setHistoryMode('30d')} variant="solid">
                    최근 30일
                </FilterPill>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                    <select
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        className="rounded-md border border-surface-border bg-surface-elevated px-2 py-1 text-xs text-content-secondary focus:border-brand focus:outline-none"
                        disabled={availableMonths.length === 0}
                        aria-label="조회할 월 선택"
                    >
                        {availableMonths.length === 0 && <option value="">데이터 없음</option>}
                        {availableMonths.map((month) => (
                            <option key={month} value={month}>{month}</option>
                        ))}
                    </select>
                    <FilterPill
                        active={historyMode === 'month'}
                        onClick={handleMonthFetch}
                        variant="solid"
                        className={availableMonths.length === 0 ? 'cursor-not-allowed opacity-50' : ''}
                    >
                        월 전체 조회
                    </FilterPill>
                </div>
            </div>

            {/* 시안 V2 KPI 4-그리드 (repeat(4, 1fr) gap=14) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                {/* KPI 1: 총 잔고 — 시안 "기간 손익" 위치 (운영 데이터: 총잔고) */}
                <KpiCard
                    label="총 잔고"
                    value={krw0(totalBalance)}
                    tone={currentMonth ? (currentMonth.pos ? 'gain' : undefined) : undefined}
                    sub={latestTotals.length > 0 ? `${latestTotals.length}개 전략` : '데이터 없음'}
                />
                {/* KPI 2: 전략 수 — 시안 "총 거래 수" 위치 (운영 데이터: 전략 수) */}
                <KpiCard
                    label="전략 수"
                    value={String(tabs.length)}
                    sub="연결된 Key Pair 기준"
                />
                {/* KPI 3: 이번 달 수익률 — 시안 "승률" 위치 (운영 데이터: FE 집계) */}
                {/* NOTE(DAS-65-G4): FE 집계 */}
                <KpiCard
                    label="이번 달 수익률"
                    value={currentMonth ? pctStr(currentMonth.pct) : '—'}
                    tone={currentMonth ? (currentMonth.pos ? 'gain' : 'loss') : undefined}
                    sub={currentMonth ? currentMonth.month : '데이터 없음'}
                />
                {/* KPI 4: 누적 기간 — 시안 "평균 보유 기간" 위치 (운영 데이터: 월수) */}
                <KpiCard
                    label="누적 기간"
                    value={`${monthlyBars.length}개월`}
                    sub="첫 스냅샷 기준"
                />
            </div>

            {/* 시안 V2 메인 차트 + 월별 미니바 사이드 (1fr / 340px) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 340px)', gap: 14 }}>
                {/* 잔고 추이 카드 */}
                <div
                    className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                    style={{ padding: 18 }}
                >
                    <CardHead
                        title="잔고 추이"
                        badge={balanceBadge}
                        sub="전략별 합산 · 주요 이벤트 마커 포함"
                        right={
                            <div style={{ display: 'flex', gap: 4 }}>
                                {['1D', '1W', '1M', '3M', '1Y'].map((r) => (
                                    <FilterPill
                                        key={r}
                                        active={chartTimeRange === r}
                                        onClick={() => setChartTimeRange(r)}
                                        variant="solid"
                                        style={{ fontSize: 11, padding: '4px 10px' }}
                                    >
                                        {r}
                                    </FilterPill>
                                ))}
                            </div>
                        }
                    />
                    <TotalBalanceChart balanceHistory={balanceHistory} />
                </div>

                {/* 월별 손익 사이드 카드 — primitives.MiniBars 296x96 */}
                <div
                    className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                    style={{ padding: 18, display: 'flex', flexDirection: 'column' }}
                >
                    <CardHead
                        title="월별 손익"
                        right={
                            <span style={{
                                fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
                                color: 'var(--color-content-muted)',
                                letterSpacing: '0.08em', textTransform: 'uppercase',
                            }}>
                                {monthlyBars.length}M
                            </span>
                        }
                    />
                    {/* NOTE(DAS-65-G4): FE 집계 — primitives.MiniBars 296x96 (시안 V2)
                        aria-label 래퍼: primitives.MiniBars가 aria-hidden="true" 고정이므로
                        접근성 레이블은 외부 div[role=img]로 제공 */}
                    <div role="img" aria-label="월별 손익 미니바 차트">
                        <MiniBars data={miniBarsData} w={296} h={96} />
                    </div>
                    <div style={{
                        display: 'flex', flexDirection: 'column', gap: 6,
                        marginTop: 14, paddingTop: 14,
                        borderTop: '1px solid var(--color-surface-border)',
                    }}>
                        {monthlyBars.length === 0 && (
                            <div style={{
                                fontSize: 12, color: 'var(--color-content-muted)',
                                textAlign: 'center', padding: '20px 0',
                            }}>
                                월별 데이터 없음
                            </div>
                        )}
                        {/* 시안 V2: 5개 표시 */}
                        {monthlyBars.slice(0, 5).map((m) => (
                            <MonthlyRow key={m.month} m={m} />
                        ))}
                    </div>
                </div>
            </div>

            {/* 활동 타임라인 — 시안 V2 Timeline 카드 */}
            <div
                className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                style={{ padding: 18 }}
            >
                <CardHead
                    title="활동 타임라인"
                    sub="거래 · 잔고 변동을 전략 단위로 (체결가/슬리피지는 BE 미제공)"
                    right={
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {/* 시안 V2: 전체/매수/매도/입출금/필터 pill */}
                            <FilterPill active variant="solid">전체</FilterPill>
                            <FilterPill variant="solid">매수</FilterPill>
                            <FilterPill variant="solid">매도</FilterPill>
                            <FilterPill variant="solid">입출금</FilterPill>
                            <button
                                type="button"
                                className="flex items-center gap-1 rounded-lg border border-surface-border px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-hover transition-colors"
                                aria-label="필터"
                            >
                                <V2Icon name="filter" size={12} />
                                필터
                            </button>
                        </div>
                    }
                />

                {/* 전략 탭 */}
                {tabs.length > 0 && (
                    <div style={{
                        display: 'flex', gap: 8, overflowX: 'auto',
                        borderBottom: '1px solid var(--color-surface-border)',
                        paddingBottom: 10, marginBottom: 18,
                    }}>
                        {tabs.map((tab) => (
                            <FilterPill
                                key={tab.key}
                                active={tab.key === resolvedActiveKey}
                                onClick={() => setActiveTabKey(tab.key)}
                                variant="solid"
                                size="md"
                                className="whitespace-nowrap"
                            >
                                {tab.label}
                            </FilterPill>
                        ))}
                    </div>
                )}

                {activeTab && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                        <HistoryChart balanceHistory={activeTab.hist} />
                        <HistoryTable balanceHistory={activeTab.hist} />
                    </div>
                )}
                {tabs.length === 0 && !loading && (
                    <div style={{
                        padding: '32px 0', textAlign: 'center',
                        color: 'var(--color-content-muted)', fontSize: 13,
                    }}>
                        히스토리 데이터가 없습니다
                    </div>
                )}
                {loading && tabs.length === 0 && <Spinner />}

                {/* 시안 V2: "더 보기" 버튼 — TODO(DAS-104): 페이지네이션 미구현 */}
                {tabs.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 14 }}>
                        <Btn variant="outline" size="sm" aria-label="더 보기">
                            더 보기
                        </Btn>
                    </div>
                )}
            </div>

            {/* 월별 잔고 요약 테이블 */}
            <div
                className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                style={{ padding: 18 }}
            >
                <CardHead
                    title="월별 잔고 요약"
                    right={monthlyBars.length > 0 && (
                        <span style={{
                            fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                            color: 'var(--color-content-muted)',
                        }}>
                            {monthlyBars.length}개월
                        </span>
                    )}
                />
                <MonthlySummaryTable balanceHistory={balanceHistory} mode="total" summary={monthlySummary} />
            </div>
        </div>
    );
}

/* ── 모바일 V3 (시안 HistoryMobileV2 매핑) ──────────────── */
function HistoryMobileV2Refreshed({ state, handlers }) {
    const {
        loading, balanceHistory, monthlySummary,
        historyMode, selectedMonth, availableMonths, activeTabKey,
    } = state;
    const {
        setHistoryMode, handleMonthChange, handleMonthFetch,
        handleRefresh, setActiveTabKey,
    } = handlers;

    /* NOTE(DAS-65-G4): FE 집계 */
    const monthlyBars = buildMonthlyBars(balanceHistory);
    const currentMonth = monthlyBars[0] || null;

    /* KPI 집계 */
    const latestTotals = (balanceHistory || []).map((arr) => arr?.[0]);
    const totalBalance = latestTotals.reduce((a, s) => a + Number(s?.total_balance || 0), 0);

    const tabs = (balanceHistory || [])
        .slice()
        .sort((a, b) => (b[0]?.total_balance || 0) - (a[0]?.total_balance || 0))
        .map((hist, i) => {
            const strategyName = hist[0]?.strategy_name || 'default';
            const fiatEx = hist[0]?.fiat_exchange;
            const riskyEx = hist[0]?.risky_exchange;
            const pairLabel = fiatEx && riskyEx ? `${fiatEx}-${riskyEx}` : fiatEx || riskyEx || 'unknown';
            return { key: String(i), label: `${strategyName} · ${pairLabel}`, hist };
        });

    const resolvedActiveKey = tabs.find((t) => t.key === activeTabKey)?.key || tabs[0]?.key;
    const activeTab = tabs.find((t) => t.key === resolvedActiveKey);

    /* primitives.MiniBars 용 data 변환 — { v: pct } */
    const miniBarsData = [...monthlyBars].reverse().map((m) => ({ v: m.pct }));

    return (
        /* 시안 .m-body gap=14 */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* 시안 V2 모바일 히어로 카드 — gradient + radial 글로우 */}
            <div
                className="rounded-2xl border border-surface-border shadow-card"
                style={{
                    padding: 18,
                    background: 'linear-gradient(135deg, var(--color-surface-card), var(--color-surface-elevated))',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <div style={{
                    position: 'absolute', inset: 0,
                    background: currentMonth?.pos
                        ? 'radial-gradient(circle at 100% 0%, var(--color-gain-subtle), transparent 60%)'
                        : currentMonth
                            ? 'radial-gradient(circle at 100% 0%, var(--color-loss-subtle), transparent 60%)'
                            : 'radial-gradient(circle at 100% 0%, var(--color-brand-subtle), transparent 60%)',
                }} aria-hidden="true" />
                <div style={{ position: 'relative' }}>
                    <div style={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', marginBottom: 8,
                    }}>
                        <KpiLabel>이번 달 손익</KpiLabel>
                        <RefreshBtn loading={loading} onClick={handleRefresh} />
                    </div>
                    <div style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 30, fontWeight: 600,
                        letterSpacing: '-0.02em',
                        color: currentMonth
                            ? (currentMonth.pos ? 'var(--color-gain)' : 'var(--color-loss)')
                            : 'var(--color-content)',
                        lineHeight: 1.05,
                    }}>
                        {currentMonth ? (currentMonth.pos ? '+' : '') + krw0(currentMonth.pnl) : '—'}
                    </div>
                    <div style={{
                        marginTop: 6, fontSize: 13,
                        color: currentMonth
                            ? (currentMonth.pos ? 'var(--color-gain)' : 'var(--color-loss)')
                            : 'var(--color-content-secondary)',
                        fontFamily: 'JetBrains Mono, monospace',
                        fontWeight: 600,
                    }}>
                        {currentMonth ? `${currentMonth.month} · ${pctStr(currentMonth.pct)}` : '데이터 없음'}
                    </div>
                </div>
            </div>

            {/* 시안 V2 모바일 미니 KPI 2-col */}
            <div style={{ display: 'flex', gap: 10 }}>
                <div
                    className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                    style={{ flex: 1, padding: 14 }}
                >
                    <KpiLabel>총 잔고</KpiLabel>
                    <div style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 18, fontWeight: 600,
                        letterSpacing: '-0.015em',
                        color: 'var(--color-content)',
                    }}>
                        {krw0(totalBalance)}
                    </div>
                    <div style={{
                        fontSize: 10, marginTop: 4,
                        color: 'var(--color-content-muted)',
                        fontFamily: 'JetBrains Mono, monospace',
                    }}>
                        {latestTotals.length > 0 ? `${latestTotals.length}개 전략` : '—'}
                    </div>
                </div>
                <div
                    className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                    style={{ flex: 1, padding: 14 }}
                >
                    <KpiLabel>누적 기간</KpiLabel>
                    <div style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 20, fontWeight: 600,
                        color: 'var(--color-content)',
                    }}>
                        {monthlyBars.length}M
                    </div>
                    <div style={{
                        fontSize: 10, marginTop: 4,
                        color: 'var(--color-content-muted)',
                        fontFamily: 'JetBrains Mono, monospace',
                    }}>
                        첫 스냅샷 기준
                    </div>
                </div>
            </div>

            {/* 조회 필터 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <FilterPill active={historyMode === '30d'} onClick={() => setHistoryMode('30d')} variant="solid">
                    최근 30일
                </FilterPill>
                <select
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    className="rounded-md border border-surface-border bg-surface-elevated px-2 py-1 text-xs text-content-secondary focus:border-brand focus:outline-none"
                    disabled={availableMonths.length === 0}
                    aria-label="조회할 월 선택"
                >
                    {availableMonths.length === 0 && <option value="">데이터 없음</option>}
                    {availableMonths.map((month) => (
                        <option key={month} value={month}>{month}</option>
                    ))}
                </select>
                <FilterPill
                    active={historyMode === 'month'}
                    onClick={handleMonthFetch}
                    variant="solid"
                    className={availableMonths.length === 0 ? 'cursor-not-allowed opacity-50' : ''}
                >
                    월 조회
                </FilterPill>
            </div>

            {/* 잔고 추이 */}
            <div
                className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                style={{ padding: 14 }}
            >
                <CardHead title="잔고 추이" />
                <TotalBalanceChart balanceHistory={balanceHistory} />
            </div>

            {/* 월별 요약 — 미니바 헤더 우측 + 목록 (시안 V2 모바일 120x36) */}
            <div
                className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                style={{ padding: 14 }}
            >
                <CardHead
                    title="월별 요약"
                    right={
                        <div style={{ width: 120 }}>
                            {/* NOTE(DAS-65-G4): primitives.MiniBars 120x36
                                aria-label 래퍼: primitives.MiniBars aria-hidden="true" 고정 */}
                            <div role="img" aria-label="월별 손익 미니바 차트">
                                <MiniBars data={miniBarsData} w={120} h={36} />
                            </div>
                        </div>
                    }
                />
                {monthlyBars.slice(0, 5).map((m) => (
                    <MonthlyRow key={m.month} m={m} />
                ))}
                {monthlyBars.length === 0 && (
                    <div style={{
                        fontSize: 12, color: 'var(--color-content-muted)',
                        padding: '12px 0',
                    }}>
                        데이터 없음
                    </div>
                )}
            </div>

            {/* 활동 카드 */}
            <div
                className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                style={{ padding: 14 }}
            >
                <CardHead
                    title="활동"
                    right={
                        <span style={{
                            fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
                            color: 'var(--color-content-muted)',
                        }}>
                            최근 10건
                        </span>
                    }
                />
                {tabs.length > 0 && (
                    <div style={{
                        display: 'flex', gap: 6, overflowX: 'auto',
                        paddingBottom: 8, marginBottom: 12,
                        borderBottom: '1px solid var(--color-surface-border)',
                    }}>
                        {tabs.map((tab) => (
                            <FilterPill
                                key={tab.key}
                                active={tab.key === resolvedActiveKey}
                                onClick={() => setActiveTabKey(tab.key)}
                                variant="solid"
                                className="whitespace-nowrap text-xs"
                            >
                                {tab.label}
                            </FilterPill>
                        ))}
                    </div>
                )}
                {activeTab && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <HistoryChart balanceHistory={activeTab.hist} />
                        <HistoryTable balanceHistory={activeTab.hist} />
                    </div>
                )}
                {tabs.length === 0 && !loading && (
                    <div style={{
                        padding: '24px 0', textAlign: 'center',
                        color: 'var(--color-content-muted)', fontSize: 12,
                    }}>
                        히스토리 데이터가 없습니다
                    </div>
                )}
            </div>

            {/* 월별 잔고 요약 테이블 */}
            <div
                className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                style={{ padding: 14 }}
            >
                <CardHead title="월별 잔고 요약" />
                <MonthlySummaryTable balanceHistory={balanceHistory} mode="total" summary={monthlySummary} />
            </div>
        </div>
    );
}

/* ── 메인 클래스 컴포넌트 ─────────────────────────────────── */
class HistoryPageV3 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            loading: false,
            lastUpdated: null,
            balanceHistory: [],
            activeTabKey: null,
            historyMode: '30d',
            selectedMonth: dayjs().format('YYYY-MM'),
            monthlySummary: [],
            availableMonths: [],
            isMobile: isMobileViewport(),
            /* 시안 V2 UI 상태 */
            chartTimeRange: '1M',   /* 잔고 추이 time-range */
        };
        this.isPolling = false;
        this.shouldPoll = true;
        this.pollTimer = null;
        this._onResize = this._onResize.bind(this);
    }

    _onResize() {
        const m = isMobileViewport();
        if (m !== this.state.isMobile) this.setState({ isMobile: m });
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('resize', this._onResize);
        this.pollData();
    }

    componentWillUnmount() {
        this.shouldPoll = false;
        clearTimeout(this.pollTimer);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        window.removeEventListener('resize', this._onResize);
    }

    onVisibilityChange = () => {
        this.shouldPoll = !document.hidden;
        if (this.shouldPoll && !this.isPolling) this.pollData();
    };

    pollData = () => {
        if (!this.shouldPoll) return;
        this.isPolling = true;
        const username = getStoredUsername();
        if (!username) { this.isPolling = false; return; }
        this.setState({ loading: true });
        const { historyMode, selectedMonth } = this.state;

        const summaryPromise = fetchMonthlySummary(username)
            .then((res) => (Array.isArray(res.data) ? res.data : []))
            .catch((err) => {
                console.error('월별 요약 로드 실패', err);
                showToast({ type: 'error', message: '월별 요약 로드에 실패했습니다.' });
                return [];
            });

        summaryPromise
            .then((summary) => {
                const monthsSet = new Set();
                summary.forEach((item) => {
                    (item?.summary || []).forEach((row) => { if (row?.month) monthsSet.add(row.month); });
                });
                const availableMonths = Array.from(monthsSet).sort((a, b) => b.localeCompare(a));
                let resolvedMonth = selectedMonth;
                if (availableMonths.length > 0 && !availableMonths.includes(resolvedMonth))
                    resolvedMonth = availableMonths[0];
                if (!resolvedMonth) resolvedMonth = availableMonths.length > 0 ? availableMonths[0] : '';
                const params = historyMode === 'month'
                    ? (resolvedMonth ? { month: resolvedMonth } : {})
                    : { start_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD') };

                return fetchBalanceHistory(username, params)
                    .then((historyResponse) => {
                        const history = Array.isArray(historyResponse.data) ? historyResponse.data : [];
                        this.setState({
                            balanceHistory: history,
                            monthlySummary: summary,
                            availableMonths,
                            selectedMonth: resolvedMonth,
                            lastUpdated: new Date(),
                        });
                    })
                    .catch((err) => {
                        console.error('히스토리 로드 실패', err);
                        showToast({ type: 'error', message: '히스토리 로드에 실패했습니다.' });
                        this.setState({ monthlySummary: summary, availableMonths, selectedMonth: resolvedMonth, lastUpdated: new Date() });
                    });
            })
            .finally(() => {
                this.isPolling = false;
                // 언마운트(shouldPoll=false) 후 setState 방지 — DAS-116 안전판
                if (!this.shouldPoll) return;
                this.setState({ loading: false });
                this.pollTimer = setTimeout(this.pollData, 5 * 60 * 1000);
            });
    };

    setHistoryMode = (mode) => { this.setState({ historyMode: mode }, () => { this.pollData(); }); };
    handleMonthChange = (event) => { this.setState({ selectedMonth: event.target.value }); };
    handleMonthFetch = () => {
        if (!this.state.selectedMonth) return;
        this.setState({ historyMode: 'month' }, () => { this.pollData(); });
    };
    onCollapse = (collapsed) => { setNavCollapsed(collapsed); this.setState({ collapsed }); };
    handleRefresh = () => { this.pollData(); };
    setActiveTabKey = (key) => { this.setState({ activeTabKey: key }); };
    setChartTimeRange = (r) => { this.setState({ chartTimeRange: r }); };

    render() {
        const { collapsed, isMobile } = this.state;

        const sharedState = { ...this.state };
        const sharedHandlers = {
            setHistoryMode: this.setHistoryMode,
            handleMonthChange: this.handleMonthChange,
            handleMonthFetch: this.handleMonthFetch,
            handleRefresh: this.handleRefresh,
            setActiveTabKey: this.setActiveTabKey,
            setChartTimeRange: this.setChartTimeRange,
        };

        return (
            <div className="min-h-screen w-full bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed ? 'sm:pl-[56px]' : 'sm:pl-[220px]'
                    }`}
                    style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
                >
                    <MaintenanceBanner />
                    <div className="mx-3 my-3 min-w-0 sm:mx-6 sm:my-5">
                        {isMobile
                            ? <HistoryMobileV2Refreshed state={sharedState} handlers={sharedHandlers} />
                            : <HistoryDesktopV2Refreshed state={sharedState} handlers={sharedHandlers} />
                        }
                    </div>
                </main>
                <ScrollToTopButton />
            </div>
        );
    }
}

export default HistoryPageV3;
